import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserBusinesses } from '../../services/businessService';
import { getAccessToken } from '../../services/authService';
import './BusinessLanding.css';

interface BusinessSummary {
  id: string;
  name: string;
  ein?: string;
  status?: string;
}

const BusinessLanding = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<BusinessSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBusinesses = async () => {
      const token = getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      try {
        const list = await getUserBusinesses();
        setBusinesses(list as BusinessSummary[]);
      } catch (err: any) {
        console.error('Error fetching businesses:', err);
        setError(err?.message || 'Unable to load your businesses.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBusinesses();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="business-landing-empty business-landing-loading">
        <div className="empty-content">
          <div className="spinner"></div>
          <p>Loading your businesses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="business-landing-empty business-landing-error">
        <div className="empty-content">
          <h2>Unable to load businesses</h2>
          <p>{error}</p>
          <div className="landing-actions">
            <button className="btn-secondary" onClick={() => window.location.reload()}>
              Retry
            </button>
            <button className="btn-primary" onClick={() => navigate('/account/business/register')}>
              Register a Business
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (businesses.length === 0) {
    return (
      <div className="business-landing-empty">
        <div className="empty-content">
          <h2>You don’t have any businesses yet</h2>
          <p>Register a business to get started.</p>
          <button className="btn-primary" onClick={() => navigate('/account/business/register')}>
            Register a Business
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="business-landing-page">
      <div className="landing-header">
        <div>
          <h1>My Businesses</h1>
          <p>Select a business to view or manage its details, or register another business.</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/account/business/register')}>
          Register a Business
        </button>
      </div>

      <div className="business-list">
        {businesses.map((business) => (
          <button
            key={business.id}
            className="business-card"
            onClick={() => navigate(`/account/business/${business.id}`)}
            type="button"
          >
            <div className="business-card-title">{business.name}</div>
            <div className="business-card-meta">
              {business.ein && <span>EIN: {business.ein}</span>}
              {business.status && <span>Status: {business.status.replace(/_/g, ' ')}</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default BusinessLanding;
